import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Cancel or recover.
// Fixture: `cancelOrRecoverAnUpload` (a long interview still processing at the
// top of the list, a slow "S3" upload that waits for Cancel upload, and two
// earlier recordings for Previous Uploads).
const CANCEL = sel("cancelUpload");
const PROCESSING_NOTICE = `${sel("editorProcessing")} .bg-green-50`;

const scene: Scene = {
  id: "cancel-or-recover-an-upload",
  title: "Cancel or recover",
  startRoute: "/cases",
  fixture: "cancelOrRecoverAnUpload",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Transcription runs on our servers, not in your browser. A one-minute narrative takes a minute or two, but a two-hour interview or a big body-cam file can take much longer. So a long job isn't always a stuck one.",
      actions: [
        { type: "waitFor", target: sel("caseListItem") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "processing",
      narration:
        "Open a case that's still processing and you'll see this screen. If it says you can safely leave this page, that's normal, so leave and come back. There's no cancel button here. Once processing has started, let it finish, then delete the case.",
      actions: [
        { type: "moveTo", target: sel("caseListItem") },
        { type: "wait", ms: 1800 },
        { type: "click", target: sel("caseListItem") },
        { type: "waitFor", target: sel("editorProcessing") },
        { type: "wait", ms: 800 },
        { type: "spotlight", target: PROCESSING_NOTICE },
        { type: "wait", ms: 6500 },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "stuck",
      narration:
        "What does mean trouble is an upload bar that stops moving while the tab is open. That usually means the connection dropped. Reload, then check Cases to see whether the case exists. A case left half-uploaded overnight is marked failed by an hourly cleanup.",
      actions: [
        { type: "moveTo", target: sel("casesNav") },
        { type: "wait", ms: 6500 },
        { type: "spotlight", target: sel("casesNav") },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
        { type: "wait", ms: 3000 },
      ],
    },
    {
      id: "upload",
      narration:
        "Say I'm sending a long interview. I'll click New Case, then Upload Files, add the recording, and hit Upload.",
      actions: [
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 1300 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 100 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        { type: "wait", ms: 400 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/interaction-witness-interview.mp3`,
          name: "witness-interview-full.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: `${sel("uploadSubmit")}:not([disabled])`, timeoutMs: 15000 },
        { type: "wait", ms: 300 },
        { type: "scrollIntoView", target: sel("uploadSubmit") },
        { type: "click", target: sel("uploadSubmit") },
        { type: "waitFor", target: CANCEL, timeoutMs: 15000 },
        { type: "wait", ms: 500 },
      ],
    },
    {
      id: "cancel",
      narration:
        "While the files are still uploading, there's a progress toast at the bottom of the screen. Click the X, Cancel upload, and it stops right away.",
      actions: [
        { type: "wait", ms: 1000 },
        { type: "moveTo", target: CANCEL },
        { type: "spotlight", target: CANCEL },
        { type: "wait", ms: 2500 },
        { type: "click", target: CANCEL },
        { type: "spotlight", target: null },
        { type: "wait", ms: 2500 },
      ],
    },
    {
      id: "trash",
      narration:
        "Cancelling aborts the upload, deletes the files already sent, stops any running transcription jobs, and moves the case to Trash.",
      actions: [
        { type: "moveTo", target: sel("trashNav") },
        { type: "wait", ms: 5000 },
        { type: "click", target: `${sel("trashNav")} button` },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "again",
      narration:
        "Then start fresh. Click New Case, then Upload Files. If the file is huge, use the Download our file compressor link first. Smaller files upload faster and fail less.",
      actions: [
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 600 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadCompressor") },
        { type: "wait", ms: 1000 },
        { type: "scrollIntoView", target: sel("uploadCompressor") },
        { type: "spotlight", target: sel("uploadCompressor") },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "previous",
      narration:
        "And click Previous Uploads to reuse a file that already reached the server, so you never send it twice.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadPrevious") },
        { type: "moveTo", target: sel("uploadPrevious") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("uploadPrevious") },
        { type: "wait", ms: 4000 },
      ],
    },
  ],
};

export default scene;
